// frontend/src/components/NotificationCenter.tsx
// Notification center dropdown component for header

import React, { useState, useEffect, useRef } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';

import NotificationBadge from './NotificationBadge';
import NotificationItem from './NotificationItem';
import {
  Notification,
  getUserNotifications,
  markNotificationAsRead,
  deleteNotification
} from '../../services/notificationApi';

const NotificationCenter: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const overlayPanelRef = useRef<OverlayPanel>(null);
  const toastRef = useRef<Toast>(null);
  const navigate = useNavigate();

  // Load notifications when panel opens
  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen]);

  // Background refresh for badge count
  useEffect(() => {
    loadNotifications(false);
    const intervalId = window.setInterval(() => {
      loadNotifications(false);
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const loadNotifications = async (showLoading = true) => {
    if (showLoading) setLoading(true);

    try {
      const response = await getUserNotifications();
      if (response.success) {
        setNotifications(response.data);
        setUnreadCount(response.unreadCount);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
      // Don't show error toast to user - gracefully handle backend unavailability
      // In production, you might want to show a subtle indicator instead
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    // If notification has an action URL, navigate there
    if (notification.action_url) {
      navigate(notification.action_url);
      overlayPanelRef.current?.hide();
    }
  };

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      const response = await markNotificationAsRead(notificationId);
      if (response.success) {
        // Update local state
        setNotifications(prev =>
          prev.map(n =>
            n.id === notificationId ? { ...n, is_read: true } : n
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Unable to update',
        detail: 'Could not mark notification as read. Please try again.',
        life: 3000
      });
    }
  };

  const handleDelete = async (notificationId: number) => {
    try {
      const response = await deleteNotification(notificationId);
      if (response.success) {
        // Remove from local state
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        // Update unread count if deleted notification was unread
        const deletedNotification = notifications.find(n => n.id === notificationId);
        if (deletedNotification && !deletedNotification.is_read) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }

        toastRef.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Notification deleted',
          life: 2000
        });
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete notification',
        life: 3000
      });
    }
  };

  const handleMarkAllRead = async () => {
    // Mark all unread notifications as read
    const unreadNotifications = notifications.filter(n => !n.is_read);
    if (unreadNotifications.length === 0) return;

    try {
      // Mark each unread notification as read
      await Promise.all(
        unreadNotifications.map(n => markNotificationAsRead(n.id))
      );

      // Update local state
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true }))
      );
      setUnreadCount(0);

      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'All notifications marked as read',
        life: 2000
      });
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to mark all notifications as read',
        life: 3000
      });
    }
  };

  const handleViewAll = () => {
    navigate('/user/notifications');
    overlayPanelRef.current?.hide();
  };

  const handleViewPreferences = () => {
    navigate('/user/preferences');
    overlayPanelRef.current?.hide();
  };

  return (
    <>
      <Toast ref={toastRef} />

      {/* Trigger Button */}
      <Button
        icon="pi pi-bell"
        className="p-link relative overflow-visible text-white hover:bg-blue-700 p-2 rounded"
        onClick={(e) => overlayPanelRef.current?.toggle(e)}
        title="Notifications"
      >
        {unreadCount > 0 && (
          <NotificationBadge count={unreadCount} />
        )}
      </Button>

      <OverlayPanel
        ref={overlayPanelRef}
        onShow={() => setIsOpen(true)}
        onHide={() => setIsOpen(false)}
        className="notification-center-panel shadow-xl rounded-xl"
        style={{ width: '420px', maxHeight: '600px', borderRadius: '12px' }}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <NotificationBadge count={unreadCount} />
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              icon="pi pi-cog"
              size="small"
              text
              className="p-1"
              onClick={handleViewPreferences}
            />
          </div>
        </div>

        {/* Content */}
        <div className="max-h-96 overflow-hidden">
          {loading && (
            <div className="flex justify-center items-center p-8">
              <ProgressSpinner
                style={{ width: '30px', height: '30px' }}
                strokeWidth="4"
              />
            </div>
          )}

          {!loading && notifications.length === 0 && (
            <div className="flex flex-col items-center justify-center p-8 text-gray-500">
              <i className="pi pi-bell text-4xl mb-3 text-gray-300"></i>
              <p className="text-center">No notifications yet</p>
              <p className="text-sm text-center mt-1">
                We'll notify you when there are updates
              </p>
            </div>
          )}

          {!loading && notifications.length > 0 && (
            <div className="max-h-96 overflow-y-auto">
              <div className="divide-y divide-gray-100">
                {notifications.slice(0, 10).map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onClick={handleNotificationClick}
                    onAction={handleNotificationClick}
                    onMarkRead={handleMarkAsRead}
                    onDelete={handleDelete}
                    showActions={true}
                  />
                ))}
                {notifications.length > 10 && (
                  <div className="p-3 text-center text-sm text-gray-500 bg-gray-50">
                    And {notifications.length - 10} more notifications...
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="flex justify-between items-center p-3 border-t bg-gray-50">
            <Button
              label="Mark All Read"
              size="small"
              text
              onClick={handleMarkAllRead}
              disabled={unreadCount === 0}
            />
            <Button
              label="View All"
              size="small"
              onClick={handleViewAll}
            />
          </div>
        )}
      </OverlayPanel>
    </>
  );
};

NotificationCenter.displayName = 'NotificationCenter';

export default NotificationCenter;
